import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { API_URL, MAIN_API_URL } from "../utils/auth";
import SEO from "../utils/SEO";
import "../../css/client/ProjectRequestForm.css";

const API_HTML_RESPONSE_MESSAGE = "The project request API returned a webpage instead of JSON. Please make sure the backend is deployed/restarted with the public intake API routes.";
const FEATURE_LOAD_ERROR_MESSAGE = "We couldn't load available features. Please refresh or try again.";
const EMPTY_FEATURE_MESSAGE = "No optional features are currently available.";

const emptyOptions = {
  plans: [],
  maintenance_plans: [],
  project_types: [],
  features: [],
  industries: [],
  business_stage_choices: [],
  content_status_choices: [],
  budget_choices: [],
  timeline_choices: [],
  contact_method_choices: [],
};

async function fetchPublicIntake(path, options = {}) {
  const primaryUrl = `${API_URL}${path}`;
  const fallbackUrl = `${MAIN_API_URL}/scd-portal${path}`;
  const primary = await fetch(primaryUrl, options);
  const primaryType = primary.headers.get("content-type") || "";

  if (primary.ok || primaryType.includes("application/json")) {
    return primary;
  }

  const body = await primary.clone().text();
  if (body.trim().startsWith("<")) {
    return fetch(fallbackUrl, options);
  }

  return primary;
}
async function readApiJson(response, fallbackMessage = "Unable to complete this request right now.") {
  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return response.json();
  }

  const text = await response.text();
  const trimmed = text.trim();
  if (trimmed.startsWith("<")) {
    throw new Error(API_HTML_RESPONSE_MESSAGE);
  }

  try {
    return JSON.parse(trimmed);
  } catch (err) {
    throw new Error(trimmed || fallbackMessage);
  }
}

function apiErrorMessage(data, fallbackMessage) {
  if (!data) return fallbackMessage;
  if (typeof data.detail === "string") return data.detail;
  if (typeof data.error === "string") return data.error;
  if (typeof data.message === "string") return data.message;
  return fallbackMessage;
}

function normalizeId(value) {
  if (value === null || value === undefined) return "";
  return String(value);
}

function normalizeChoice(item) {
  const value = normalizeId(item?.value ?? item?.id ?? item?.slug ?? item?.name ?? item?.label);
  return {
    ...item,
    value,
    id: item?.id ?? value,
    label: item?.label || item?.name || value,
    name: item?.name || item?.label || value,
  };
}

function normalizePlanFeature(item) {
  const value = normalizeId(item?.value ?? item?.id ?? item?.feature_id ?? item?.slug ?? item?.name);
  return {
    ...item,
    value,
    id: item?.id ?? item?.feature_id ?? value,
    name: item?.name || item?.label || value,
    included: Boolean(item?.included),
    addon_price: item?.addon_price ?? "0.00",
    extra_days: item?.extra_days ?? "0.0",
    requires_quote: Boolean(item?.requires_quote),
  };
}

function normalizePlan(item) {
  const value = normalizeId(item?.value ?? item?.id ?? item?.slug ?? item?.name);
  const features = Array.isArray(item?.features) ? item.features.map(normalizePlanFeature) : [];
  return {
    ...item,
    value,
    id: item?.id ?? value,
    name: item?.name || item?.label || value,
    included_feature_ids: (item?.included_feature_ids || []).map(normalizeId).filter(Boolean),
    features,
  };
}

export function normalizeProjectFormOptions(data = {}) {
  const rawFeatures = data.features || data.intake_features || data.available_features || data.feature_options || [];
  const featureList = Array.isArray(rawFeatures) ? rawFeatures : [];
  return {
    ...emptyOptions,
    ...data,
    plans: (data.plans || data.project_plans || []).map(normalizePlan),
    maintenance_plans: (data.maintenance_plans || data.maintenancePlans || []).map(normalizePlan),
    project_types: (data.project_types || data.projectTypes || []).map(normalizeChoice),
    features: featureList.map(normalizeChoice),
    industries: (data.industries || []).map(normalizeChoice),
    business_stage_choices: (data.business_stage_choices || []).map(normalizeChoice),
    content_status_choices: (data.content_status_choices || []).map(normalizeChoice),
    budget_choices: (data.budget_choices || []).map(normalizeChoice),
    timeline_choices: (data.timeline_choices || []).map(normalizeChoice),
    contact_method_choices: (data.contact_method_choices || []).map(normalizeChoice),
  };
}

const emptyForm = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  preferred_contact_method: "email",
  business_name: "",
  industry: "",
  business_stage: "",
  current_website: "",
  social_media: "",
  selected_plan: "",
  maintenance_plan: "",
  project_types: [],
  project_goal: "",
  project_description: "",
  features: [],
  has_branding: "no",
  inspiration_links: "",
  content_status: "",
  budget: "",
  timeline: "",
};

export default function ProjectRequestForm() {
  const { partnerId } = useParams();
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [featureLoadError, setFeatureLoadError] = useState("");
  const [options, setOptions] = useState(emptyOptions);
  const [formData, setFormData] = useState(emptyForm);

  const partnerLabel = useMemo(
    () => partnerId ? `Referral code: ${partnerId}` : "Direct project request",
    [partnerId]
  );

  const selectedPlan = useMemo(
    () => options.plans.find((plan) => String(plan.value) === String(formData.selected_plan)),
    [options.plans, formData.selected_plan]
  );

  const lockedFeatureIds = useMemo(
    () => new Set((selectedPlan?.included_feature_ids || []).map(String)),
    [selectedPlan]
  );

  const selectedPlanFeatureMap = useMemo(() => {
    const map = new Map();
    (selectedPlan?.features || []).forEach((feature) => map.set(String(feature.value || feature.id), feature));
    return map;
  }, [selectedPlan]);

  useEffect(() => {
    async function loadOptions() {
      try {
        const response = await fetchPublicIntake("/project-form-options/");
        const data = await readApiJson(response, "Failed to load form options.");
        if (!response.ok) throw new Error(apiErrorMessage(data, "Failed to load form options."));
        setOptions(normalizeProjectFormOptions(data));
        setFeatureLoadError("");
      } catch (err) {
        setError(err.message);
        setFeatureLoadError(FEATURE_LOAD_ERROR_MESSAGE);
      } finally {
        setLoadingOptions(false);
      }
    }
    loadOptions();
  }, []);

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }

  function handlePlanSelect(planId) {
    const plan = options.plans.find((item) => String(item.value) === String(planId));
    const includedIds = (plan?.included_feature_ids || []).map(String);
    setFormData((prev) => {
      const optionalSelections = prev.features.filter((id) => !lockedFeatureIds.has(String(id)));
      return {
        ...prev,
        selected_plan: planId,
        features: Array.from(new Set([...optionalSelections, ...includedIds])),
      };
    });
  }

  function toggleListValue(name, value) {
    const normalizedValue = String(value);
    if (name === "features" && lockedFeatureIds.has(normalizedValue)) return;

    setFormData((prev) => {
      const exists = prev[name].includes(normalizedValue);
      return {
        ...prev,
        [name]: exists
          ? prev[name].filter((item) => item !== normalizedValue)
          : [...prev[name], normalizedValue],
      };
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setSuccess("");

    try {
      const response = await fetchPublicIntake("/project-request/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          partner_id: partnerId || "",
        }),
      });
      const data = await readApiJson(response, "Unable to submit request.");
      if (!response.ok) throw new Error(apiErrorMessage(data, "Unable to submit request."));
      setSuccess(data.partner_attached
        ? "Your project request has been submitted and attached to your referral partner."
        : "Your project request has been submitted successfully.");
      setFormData(emptyForm);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (loadingOptions) {
    return <div className="project-request-page"><div className="spinner-border" /></div>;
  }

  return (
    <>
    <SEO
      title="Start a Project | South Central Development"
      description="Request a website, software project, ecommerce build, mobile app, or maintenance plan from South Central Development."
      path={partnerId ? `/new-project/${partnerId}` : "/new-project"}
    />
    <main className="project-request-page">
      <section className="project-request-card">
        <div className="project-request-header">
          <span>{partnerLabel}</span>
          <h1>Start Your Project</h1>
          <p>Tell South Central Development what you need and we will review your request.</p>
        </div>

        {success && <div className="alert alert-success">{success}</div>}
        {error && <div className="alert alert-danger">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h2>Contact Information</h2>
            <div className="form-grid two">
              <label>First Name<input name="first_name" value={formData.first_name} onChange={handleChange} required /></label>
              <label>Last Name<input name="last_name" value={formData.last_name} onChange={handleChange} required /></label>
              <label>Email<input type="email" name="email" value={formData.email} onChange={handleChange} required /></label>
              <label>Phone<input name="phone" value={formData.phone} onChange={handleChange} /></label>
              <label>Preferred Contact<select name="preferred_contact_method" value={formData.preferred_contact_method} onChange={handleChange}>{options.contact_method_choices.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
            </div>
          </div>

          <div className="form-section">
            <h2>Business Information</h2>
            <div className="form-grid two">
              <label>Business Name<input name="business_name" value={formData.business_name} onChange={handleChange} /></label>
              <label>Industry<select name="industry" value={formData.industry} onChange={handleChange}><option value="">Select industry</option>{options.industries.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
              <label>Business Stage<select name="business_stage" value={formData.business_stage} onChange={handleChange}><option value="">Select stage</option>{options.business_stage_choices.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
              <label>Current Website<input name="current_website" value={formData.current_website} onChange={handleChange} placeholder="https://" /></label>
              <label className="full-field">Social Media<input name="social_media" value={formData.social_media} onChange={handleChange} placeholder="https://" /></label>
            </div>
          </div>

          <div className="form-section">
            <h2>Project Type</h2>
            {options.project_types.length === 0 ? (
              <div className="empty-state">Project type options are currently unavailable.</div>
            ) : (
              <div className="choice-grid">
                {options.project_types.map((projectType) => (
                  <label className={`choice-card ${formData.project_types.includes(String(projectType.value)) ? "selected" : ""}`} key={projectType.value}>
                    <input type="checkbox" aria-label={projectType.name || projectType.label} checked={formData.project_types.includes(String(projectType.value))} onChange={() => toggleListValue("project_types", String(projectType.value))} />
                    <strong>{projectType.name || projectType.label}</strong>
                    {projectType.description && <small>{projectType.description}</small>}
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="form-section">
            <h2>Project Package</h2>
            <div className="choice-grid plan-grid">
              {options.plans.map((plan) => (
                <label className={`choice-card plan-card ${String(formData.selected_plan) === String(plan.value) ? "selected" : ""}`} key={plan.value}>
                  <input type="radio" name="selected_plan" aria-label={plan.name} checked={String(formData.selected_plan) === String(plan.value)} onChange={() => handlePlanSelect(String(plan.value))} required />
                  <span>
                    <strong>{plan.name}</strong>
                    <em>{plan.display_price}</em>
                    {plan.description && <small>{plan.description}</small>}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-section">
            <h2>Project Goals</h2>
            <div className="form-grid">
              <label>Primary Goal<input name="project_goal" value={formData.project_goal} onChange={handleChange} placeholder="Launch a website, build a portal, automate a workflow..." /></label>
              <label>Project Description<textarea name="project_description" value={formData.project_description} onChange={handleChange} required placeholder="Tell us what you need built, changed, or improved." /></label>
            </div>
          </div>

          <div className="form-section">
            <h2>Features</h2>
            {selectedPlan && <p className="section-note">Features included with {selectedPlan.name} are selected automatically.</p>}
            {featureLoadError ? (
              <div className="empty-state error-state">{featureLoadError}</div>
            ) : options.features.length === 0 ? (
              <div className="empty-state">{EMPTY_FEATURE_MESSAGE}</div>
            ) : (
              <div className="choice-grid">
                {options.features.map((feature) => {
                  const featureId = String(feature.value || feature.id);
                  const locked = lockedFeatureIds.has(featureId);
                  const planFeature = selectedPlanFeatureMap.get(featureId);
                  return (
                    <label className={`choice-card ${locked ? "locked" : ""}`} key={featureId}>
                      <input type="checkbox" aria-label={feature.name || feature.label} checked={formData.features.includes(featureId)} disabled={locked} onChange={() => toggleListValue("features", featureId)} />
                      <strong>{feature.name || feature.label}</strong>
                      {feature.category && <span className="feature-category">{feature.category}</span>}
                      {locked && <span className="included-pill">Included</span>}
                      {!locked && planFeature && !planFeature.requires_quote && <span className="addon-pill">+${Number(planFeature.addon_price).toLocaleString()}</span>}
                      {!locked && planFeature?.requires_quote && <span className="addon-pill">Quote</span>}
                      {feature.description && <small>{feature.description}</small>}
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div className="form-section">
            <h2>Maintenance Plan</h2>
            <p className="section-note">Optional ongoing care after launch.</p>
            <div className="choice-grid plan-grid">
              <label className={`choice-card plan-card ${formData.maintenance_plan === "" ? "selected" : ""}`}>
                <input type="radio" name="maintenance_plan" checked={formData.maintenance_plan === ""} onChange={() => setFormData((prev) => ({ ...prev, maintenance_plan: "" }))} />
                <span><strong>No maintenance plan yet</strong><small>We can discuss support after reviewing the project.</small></span>
              </label>
              {options.maintenance_plans.map((plan) => (
                <label className={`choice-card plan-card ${String(formData.maintenance_plan) === String(plan.value) ? "selected" : ""}`} key={plan.value}>
                  <input type="radio" name="maintenance_plan" aria-label={plan.name} checked={String(formData.maintenance_plan) === String(plan.value)} onChange={() => setFormData((prev) => ({ ...prev, maintenance_plan: String(plan.value) }))} />
                  <span>
                    <strong>{plan.name}</strong>
                    <em>{plan.display_price}</em>
                    {plan.description && <small>{plan.description}</small>}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-section">
            <h2>Design and Content</h2>
            <div className="form-grid two">
              <label>Do you have branding?<select name="has_branding" value={formData.has_branding} onChange={handleChange}><option value="no">No</option><option value="yes">Yes</option></select></label>
              <label>Content Status<select name="content_status" value={formData.content_status} onChange={handleChange}><option value="">Select status</option>{options.content_status_choices.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
              <label className="full-field">Inspiration Links<textarea name="inspiration_links" value={formData.inspiration_links} onChange={handleChange} placeholder="One link per line." /></label>
            </div>
          </div>

          <div className="form-section">
            <h2>Budget and Timeline</h2>
            <div className="form-grid two">
              <label>Budget<select name="budget" value={formData.budget} onChange={handleChange}><option value="">Select budget</option>{options.budget_choices.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
              <label>Timeline<select name="timeline" value={formData.timeline} onChange={handleChange}><option value="">Select timeline</option>{options.timeline_choices.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
            </div>
          </div>

          <button className="btn btn-primary btn-lg w-100" disabled={submitting}>{submitting ? "Submitting..." : "Submit Project Request"}</button>
        </form>
      </section>
    </main>
    </>
  );
}
